import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Bouton, Champ, Etiquette, Pastille, Saisie } from '../components/ui/Base'
import { Bandeau, EtatVide, MessageErreur } from '../components/ui/Etats'
import { Confirmation, Modale } from '../components/ui/Modale'
import { SqueletteTableau } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import { ApiError } from '../lib/api/erreurs'
import type { Page, TypeSoin } from '../lib/api/types'
import { formatDuree, formatMontant } from '../lib/formatters'

/**
 * Référentiel des actes.
 *
 * ============================================================================
 * C'est le premier écran qu'un cabinet neuf doit remplir, et l'enchaînement du
 * contrat l'explique :
 *
 *     type de soin → ordonnance → ligne d'ordonnance → planning généré
 *
 * Sans acte saisi, aucune ordonnance n'est possible ; sans ordonnance, aucun
 * planning. Le cabinet reste bloqué au premier pas.
 * ============================================================================
 */

const schema = z.object({
  code: z
    .string()
    .min(1, 'Le code est obligatoire.')
    .max(20, 'Vingt caractères au maximum.')
    .regex(/^[A-Za-z0-9_-]+$/, 'Lettres, chiffres, tiret et tiret bas uniquement.'),
  libelle: z.string().min(1, 'Le libellé est obligatoire.').max(200),
  description: z.string().optional(),
  duree_minutes: z.coerce
    .number()
    .int('Une durée entière, en minutes.')
    .min(1, 'Au moins une minute.')
    .max(24 * 60, 'Une journée au maximum.'),
  cotation_ngap: z.string().max(20).optional(),
  coefficient: z.coerce.number().gt(0, 'Le coefficient doit être positif.'),
  tarif_base: z.coerce.number().gt(0, 'Le tarif doit être positif.'),
  couleur: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Une couleur au format #RRGGBB.'),
})

type Formulaire = z.infer<typeof schema>

/** Les cinq actes du contrat, proposés en un clic à un cabinet qui démarre. */
const ACTES_COURANTS: Formulaire[] = [
  { code: 'INJ', libelle: 'Injection sous-cutanée', description: 'Insuline, anticoagulant.', duree_minutes: 15, cotation_ngap: 'AMI', coefficient: 1, tarif_base: 3.15, couleur: '#2563eb' },
  { code: 'PANS', libelle: 'Pansement', description: 'Plaie simple ou complexe.', duree_minutes: 30, cotation_ngap: 'AMI', coefficient: 2, tarif_base: 6.3, couleur: '#dc2626' },
  { code: 'TOIL', libelle: 'Toilette et nursing', description: 'Toilette complète.', duree_minutes: 45, cotation_ngap: 'AIS', coefficient: 3, tarif_base: 7.95, couleur: '#16a34a' },
  { code: 'PERF', libelle: 'Perfusion', description: 'Pose et surveillance.', duree_minutes: 40, cotation_ngap: 'AMI', coefficient: 4, tarif_base: 12.6, couleur: '#9333ea' },
  { code: 'SURV', libelle: 'Surveillance clinique', description: 'Distribution de traitement.', duree_minutes: 20, cotation_ngap: 'AMI', coefficient: 1.5, tarif_base: 4.72, couleur: '#ea580c' },
]

const VALEURS_INITIALES: Formulaire = {
  code: '',
  libelle: '',
  description: '',
  duree_minutes: 15,
  cotation_ngap: 'AMI',
  coefficient: 1,
  tarif_base: 0,
  couleur: '#2563eb',
}

export function TypesSoins() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [enEdition, setEnEdition] = useState<TypeSoin | null>(null)
  const [formulaireOuvert, setFormulaireOuvert] = useState(false)
  const [aSupprimer, setASupprimer] = useState<TypeSoin | null>(null)

  const requete = useQuery({
    queryKey: cles.typesSoins.liste(),
    queryFn: ({ signal }) => api.get<Page<TypeSoin>>('/types-soins', signal),
  })

  const formulaire = useForm<Formulaire>({
    resolver: zodResolver(schema),
    defaultValues: VALEURS_INITIALES,
  })

  useEffect(() => {
    if (!formulaireOuvert) return
    formulaire.reset(
      enEdition
        ? {
            code: enEdition.code,
            libelle: enEdition.libelle,
            description: enEdition.description ?? '',
            duree_minutes: enEdition.duree_minutes,
            cotation_ngap: enEdition.cotation_ngap ?? '',
            coefficient: Number(enEdition.coefficient),
            tarif_base: Number(enEdition.tarif_base),
            couleur: enEdition.couleur,
          }
        : VALEURS_INITIALES,
    )
  }, [formulaireOuvert, enEdition, formulaire])

  const enregistrer = useMutation({
    mutationFn: (valeurs: Formulaire) => {
      const corps = {
        ...valeurs,
        description: valeurs.description || null,
        cotation_ngap: valeurs.cotation_ngap || null,
        // L'API attend des décimales en chaîne : jamais de flottant pour de
        // l'argent (§4).
        coefficient: valeurs.coefficient.toFixed(2),
        tarif_base: valeurs.tarif_base.toFixed(2),
      }
      return enEdition
        ? api.patch<TypeSoin>(`/types-soins/${enEdition.id}`, corps)
        : api.post<TypeSoin>('/types-soins', corps)
    },
    onSuccess: (soin) => {
      toast(enEdition ? `${soin.code} modifié.` : `${soin.code} ajouté.`, 'succes')
      setFormulaireOuvert(false)
      setEnEdition(null)
      void queryClient.invalidateQueries({ queryKey: cles.typesSoins.tout })
    },
    onError: (erreur) => {
      // Le backend désigne le champ fautif : on le pose là où l'utilisateur
      // regarde, plutôt que dans un bandeau en haut de page.
      if (erreur instanceof ApiError && erreur.champ === 'code') {
        formulaire.setError('code', { message: erreur.message })
        return
      }
      toast(erreur instanceof Error ? erreur.message : 'Enregistrement impossible.', 'erreur')
    },
  })

  const supprimer = useMutation({
    mutationFn: (id: string) => api.delete(`/types-soins/${id}`),
    onSuccess: () => {
      toast('Acte retiré.', 'succes')
      setASupprimer(null)
      void queryClient.invalidateQueries({ queryKey: cles.typesSoins.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Suppression impossible.', 'erreur'),
  })

  const installerLesCinq = useMutation({
    mutationFn: async () => {
      for (const acte of ACTES_COURANTS) {
        await api.post<TypeSoin>('/types-soins', {
          ...acte,
          coefficient: acte.coefficient.toFixed(2),
          tarif_base: acte.tarif_base.toFixed(2),
        })
      }
    },
    onSuccess: () => {
      toast('Les cinq actes courants ont été ajoutés.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.typesSoins.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Installation impossible.', 'erreur'),
  })

  const actes = requete.data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Types de soins</h1>
          <p className="text-sm text-slate-600">
            Le référentiel des actes. C&apos;est lui qui alimente les ordonnances.
          </p>
        </div>
        <Bouton
          onClick={() => {
            setEnEdition(null)
            setFormulaireOuvert(true)
          }}
        >
          Ajouter un acte
        </Bouton>
      </div>

      {requete.isPending ? (
        <SqueletteTableau colonnes={5} lignes={5} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : actes.length === 0 ? (
        <div className="space-y-4">
          <Bandeau ton="info">
            Rien ne peut être planifié tant qu&apos;aucun acte n&apos;est déclaré : une
            ordonnance prescrit un <strong>soin</strong>, et c&apos;est de là que naît le
            planning.
          </Bandeau>
          <EtatVide
            icone="💉"
            titre="Aucun acte déclaré"
            description="Commencez par les cinq actes les plus courants — vous pourrez les ajuster ensuite."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Bouton
                  onClick={() => installerLesCinq.mutate()}
                  disabled={installerLesCinq.isPending}
                >
                  {installerLesCinq.isPending
                    ? 'Installation…'
                    : 'Ajouter les cinq actes courants'}
                </Bouton>
                <Bouton
                  variante="secondaire"
                  onClick={() => {
                    setEnEdition(null)
                    setFormulaireOuvert(true)
                  }}
                >
                  Saisir un acte
                </Bouton>
              </div>
            }
          />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <caption className="sr-only">Référentiel des actes de soin</caption>
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-2.5">Code</th>
                <th scope="col" className="px-4 py-2.5">Libellé</th>
                <th scope="col" className="px-4 py-2.5">Durée</th>
                <th scope="col" className="px-4 py-2.5">Cotation</th>
                <th scope="col" className="px-4 py-2.5 text-right">Tarif</th>
                <th scope="col" className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {actes.map((acte) => (
                <tr key={acte.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2 font-mono font-medium text-slate-900">
                      <Pastille couleur={acte.couleur} titre={acte.couleur} />
                      {acte.code}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-800">
                    {acte.libelle}
                    {!acte.actif ? (
                      <Etiquette>Inactif</Etiquette>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{formatDuree(acte.duree_minutes)}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {acte.cotation_ngap ?? '—'}
                    <span className="ml-1 text-xs text-slate-400">× {acte.coefficient}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">
                    {formatMontant(acte.tarif_base)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <Bouton
                        variante="discret"
                        onClick={() => {
                          setEnEdition(acte)
                          setFormulaireOuvert(true)
                        }}
                      >
                        Modifier
                      </Bouton>
                      <Bouton variante="discret" onClick={() => setASupprimer(acte)}>
                        Retirer
                      </Bouton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Formulaire ── */}
      <Modale
        ouverte={formulaireOuvert}
        titre={enEdition ? `Modifier ${enEdition.code}` : 'Nouvel acte'}
        onFermer={() => setFormulaireOuvert(false)}
      >
        <form
          onSubmit={formulaire.handleSubmit((valeurs) => enregistrer.mutate(valeurs))}
          className="space-y-4"
          noValidate
        >
          <div className="grid grid-cols-2 gap-3">
            <Champ
              libelle="Code"
              id="code"
              erreur={formulaire.formState.errors.code?.message}
              aide="Court et unique : INJ, PANS…"
              obligatoire
            >
              <Saisie id="code" className="font-mono uppercase" {...formulaire.register('code')} />
            </Champ>
            <Champ libelle="Couleur" id="couleur" erreur={formulaire.formState.errors.couleur?.message}>
              <Saisie id="couleur" type="color" className="h-10 p-1" {...formulaire.register('couleur')} />
            </Champ>
          </div>

          <Champ
            libelle="Libellé"
            id="libelle"
            erreur={formulaire.formState.errors.libelle?.message}
            obligatoire
          >
            <Saisie id="libelle" {...formulaire.register('libelle')} />
          </Champ>

          <Champ libelle="Description" id="description">
            <Saisie id="description" {...formulaire.register('description')} />
          </Champ>

          <div className="grid grid-cols-2 gap-3">
            <Champ
              libelle="Durée standard (minutes)"
              id="duree"
              erreur={formulaire.formState.errors.duree_minutes?.message}
              obligatoire
            >
              <Saisie id="duree" type="number" min={1} {...formulaire.register('duree_minutes')} />
            </Champ>
            <Champ libelle="Cotation NGAP" id="cotation" aide="AMI, AIS…">
              <Saisie id="cotation" {...formulaire.register('cotation_ngap')} />
            </Champ>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Champ
              libelle="Coefficient"
              id="coefficient"
              erreur={formulaire.formState.errors.coefficient?.message}
            >
              <Saisie id="coefficient" type="number" step="0.01" min="0.01" {...formulaire.register('coefficient')} />
            </Champ>
            <Champ
              libelle="Tarif de base (€)"
              id="tarif"
              erreur={formulaire.formState.errors.tarif_base?.message}
              obligatoire
            >
              <Saisie id="tarif" type="number" step="0.01" min="0.01" {...formulaire.register('tarif_base')} />
            </Champ>
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <Bouton variante="secondaire" type="button" onClick={() => setFormulaireOuvert(false)}>
              Annuler
            </Bouton>
            {/* Bouton désactivé pendant la mutation : une double soumission
                créerait deux actes de même code. */}
            <Bouton type="submit" disabled={enregistrer.isPending}>
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Bouton>
          </div>
        </form>
      </Modale>

      <Confirmation
        ouverte={aSupprimer !== null}
        titre={`Retirer l'acte ${aSupprimer?.code ?? ''} ?`}
        description={
          `« ${aSupprimer?.libelle ?? ''} » ne sera plus proposé à la saisie. ` +
          "S'il est déjà utilisé par une ordonnance, il est seulement désactivé — l'historique reste lisible."
        }
        libelleConfirmation="Retirer"
        danger
        enCours={supprimer.isPending}
        onConfirmer={() => aSupprimer && supprimer.mutate(aSupprimer.id)}
        onAnnuler={() => setASupprimer(null)}
      />
    </div>
  )
}
